import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import RevenuePath from "../revenue-path/RevenuePath";

const HERO_STAGES = [
  { label: "Attention", state: "healthy" },
  { label: "Inquiry", state: "healthy" },
  { label: "Response", state: "friction" },
  { label: "Booking", state: "leak" },
  { label: "Payment", state: "broken" },
  { label: "Revenue", state: "recovered" },
];

/**
 * Homepage hero — leads with the editorial brand truth and the signature
 * Revenue Path visualization. Primary CTA is the Revenue Leak Audit.
 */
export default function HomeHero() {
  const scrollToSystem = () => {
    if (typeof document === "undefined") return;
    document.getElementById("revenue-leak-audit")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="home-hero-section"
      className="relative overflow-hidden bg-sand-100 pt-16 sm:pt-20 md:pt-24 pb-14 sm:pb-20 md:pb-24 scroll-mt-16"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-10">
        <div className="reveal">
          <p className="eyebrow">The Revenue Path™</p>
          <h1
            data-testid="home-hero-headline"
            className="font-display mt-6 sm:mt-7 text-[2.6rem] sm:text-6xl lg:text-7xl xl:text-[5rem] leading-[1.02] tracking-[-0.015em] text-ink max-w-4xl"
          >
            Interest isn&rsquo;t revenue.
            <span className="block text-ink-muted">
              The <span className="text-clay italic">path</span> between them is
              where businesses leak money.
            </span>
          </h1>
          <p
            data-testid="home-hero-subheadline"
            className="mt-6 sm:mt-8 text-[15px] sm:text-base md:text-lg text-ink-muted max-w-2xl leading-relaxed"
          >
            The Modern Exit identifies and repairs the systems between attention,
            inquiry, booking, payment, follow-up, and repeat business — so
            customers actually move from interest to cash.
          </p>

          <div className="mt-9 sm:mt-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <Link
              to="/revenue-leak-audit"
              data-testid="home-hero-primary-cta"
              data-magnetic
              className="group inline-flex items-center justify-center gap-2 rounded-none bg-ink-deep text-sand-50 px-8 py-4 text-[15px] font-medium hover:bg-ink transition-colors min-h-[54px]"
            >
              Find My Revenue Leak
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <button
              type="button"
              onClick={scrollToSystem}
              data-testid="home-hero-secondary-cta"
              className="group inline-flex items-center justify-center gap-2 px-2 py-4 text-[15px] font-medium text-ink border-b border-transparent hover:border-clay transition-colors min-h-[54px]"
            >
              See How the System Works
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-clay">↓</span>
            </button>
          </div>
        </div>

        {/* Signature Revenue Path visualization */}
        <div className="reveal mt-14 sm:mt-16 md:mt-20">
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">
              Path 01 · Live diagnostic
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay">
              Leak detected → recovered
            </span>
          </div>
          <div className="border-y border-sand-300 py-8 sm:py-10 overflow-x-auto">
            <div className="min-w-[560px]">
              <RevenuePath stages={HERO_STAGES} />
            </div>
          </div>
          <p className="mt-4 text-[13px] sm:text-sm text-ink-muted/90 max-w-xl leading-relaxed">
            Every handoff is a chance to lose the customer. The leak is usually
            between the platforms — not inside them.
          </p>
        </div>
      </div>
    </section>
  );
}
